import {
	TableCell,
	TableRow,
	Typography,
	Box,
	Collapse,
	Checkbox,
	IconButton,
	Link as MaterialLink,
	Tooltip,
} from "@mui/material";
import { useState, useEffect, useMemo, useContext } from "react";
import { ReactCountryFlag } from "react-country-flag";
import {
	primaryColor,
	grey,
	successColor,
	warningPrimary,
	dangerPrimary,
} from "../../../core/theme";
import { Tender } from "../tender.module";
import CommentIcon from "@mui/icons-material/Comment";
import FileIcon from "@mui/icons-material/AttachFile";
import PricingStatusCell from "./PricingStatusCell";
import { Link } from "react-router-dom";
import TextVerticalClamp from "../../../common/components/TextVerticalClamp";
import { useAppDispatch } from "../../../common/hooks/default";
import { updateTenders, loadTenderPricings } from "../tender.thunk";
import { useSelector } from "react-redux";
import { CollapseArrow } from "../../../common/components/CollapseArrow";
import { If } from "../../../common/components/If";
import { formatDecimal } from "../../../common/utils/formatDecimal";
import PricingTable from "./PricingTable";
import usePrevious from "../../../common/hooks/usePrevious";
import { selectAreTenderPricingsLoading } from "../tender.selector";
import { FlashUpdateTableCell } from "../../../common/components/FlashUpdateTableCell";
import {
	selectAuthenticatedUser,
	selectUserGroups,
} from "../../authentication/auth.selector";
import { getTenderColorByStatusOrDeadline } from "../tender.constant";
import * as _ from "lodash";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import TenderActionMenu from "./workflow_view/TenderActionMenu";
import { TenderPageContext } from "../TenderPage";
import { ParserSubmissionStatusBadge } from "./ParserSubmissionStatusBadge";
import ErrorIcon from "@mui/icons-material/Error";

const style: any = {
	rowContainer: {
		padding: "0 8px",
		border: `1px solid ${grey}`,
	},
	tableCell: {
		whiteSpace: "pre-wrap",
		padding: "0 0 0 10px",
		"& svg, & img": {
			float: "left",
			padding: "2px 5px 0 0",
		},
		"& p": {
			overflow: "hidden",
		},
	},
	menuCell: {
		padding: 0,
		width: "66px",
	},
	menuItem: {
		"&:hover": {
			borderLeft: `2px ${primaryColor} solid`,
		},
	},
	collapsibleCell: {
		padding: 0,
		border: 0,
	},
	pricingTableContainer: {
		overflowX: "auto",
		overflowY: "hidden",
		"--ms-overflow-style": "none" /* IE and Edge */,
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": {
			display: "none",
		},
		background: "white",
		padding: "8px",
		position: "relative",
		bottom: "6px",
	},
	pickupMe: {
		opacity: 0,
		"&:hover": {
			opacity: 1,
			border: "1px dashed rgba(0,0,0,0.2)",
			padding: "18px",
			textAlign: "center",
		},
	},
	checkICon: {
		color: successColor,
	},
	nokIcon: {
		color: warningPrimary,
	},
	errorIcon: {
		color: dangerPrimary,
	},
};

export default function Row({
	tender,
	colsLength,
	targetId,
	selectedIds,
	toggleTenderSelection,
}: {
	tender: Tender;
	colsLength: number;
	targetId?: string;
	selectedIds: number[];
	toggleTenderSelection: (tender: Tender) => void;
}) {
	const [open, setOpen] = useState(false);
	const wasOpen = usePrevious(open);
	const dispatch = useAppDispatch();
	const currentUser = useSelector(selectAuthenticatedUser);
	const pricingsAreLoading = useSelector(
		selectAreTenderPricingsLoading(tender.id)
	);
	const isUserTrader = useSelector(selectUserGroups)?.isTrader;
	const tenderPageContext = useContext(TenderPageContext);

	useEffect(() => {
		if (open && !wasOpen) {
			dispatch(loadTenderPricings(tender.id));
		}
	}, [open, wasOpen]);

	const pickupAction = useMemo(
		() =>
			_.find(
				tender.actions_blotter,
				(action) => action.action === "PICKUP"
			),
		[tender]
	);

	return (
		<>
			<TableRow
				id={targetId || ""}
				sx={{
					...style.rowContainer,
					backgroundColor: getTenderColorByStatusOrDeadline(tender),
					boxShadow: `inset 0px 0px 0px 5px ${getTenderColorByStatusOrDeadline(
						tender
					)}`,
				}}
			>
				<TableCell align="center">
					<Checkbox
						checked={selectedIds.includes(tender?.id)}
						onChange={() => toggleTenderSelection(tender)}
					/>
				</TableCell>
				<TableCell>
					<CollapseArrow
						isOpen={open}
						setIsOpen={setOpen}
					></CollapseArrow>
				</TableCell>
				<FlashUpdateTableCell
					field={tender?.status}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<PricingStatusCell
						status={(tender?.status as any) ?? "UNDER_PRICING"}
					></PricingStatusCell>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.name}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Link to={`/tenders/${tender?.id}/my-pricings`}>
						{tender?.name}
					</Link>
					<If condition={tender?.is_gemforce_opportunity_closed}>
						<Tooltip title={tender.cor_cer_sync_error}>
							<IconButton>
								<ErrorIcon sx={style.errorIcon} />
							</IconButton>
						</Tooltip>
					</If>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.account?.name}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<TextVerticalClamp maxLine={3}>
						<Typography variant="subtitle2" component="p">
							{tender?.account?.name}
						</Typography>
					</TextVerticalClamp>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.countries?.[0] ?? ""}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<ReactCountryFlag
						countryCode={
							tender?.countries ? tender?.countries[0] : ""
						}
						svg
						title={tender?.countries ? tender?.countries[0] : ""}
					/>{" "}
					{tender?.countries ? tender?.countries[0] : ""}
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.currency ?? ""}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					{tender?.currency}
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.installed_capacity_mw}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{formatDecimal(tender?.installed_capacity_mw)}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.site_count}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender.count_attached_sites}/{tender?.site_count}
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={tender?.direction}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.direction}
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={
						tender?.technologies?.length > 1
							? "Mixed"
							: tender?.technologies?.[0]
					}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.technologies?.length > 1
							? "Mixed"
							: tender?.technologies}
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={tender?.originator?.gaia_id}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.originator?.first_name}{" "}
						{tender?.originator?.last_name}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.trader?.gaia_id}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<If condition={tender?.trader}>
						<Typography variant="subtitle2" component="p">
							{tender?.trader?.first_name}{" "}
							{tender?.trader?.last_name}
						</Typography>
					</If>
					<If
						condition={
							!tender?.trader && isUserTrader && pickupAction
						}
					>
						<Typography
							variant="subtitle2"
							component="p"
							sx={style.pickupMe}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								dispatch(
									updateTenders(
										[tender.id],
										pickupAction!,
										currentUser?.name
									)
								);
							}}
						>
							Pickup
						</Typography>
					</If>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.due_date}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.due_date}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.validity_date}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.validity_date}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.creation_date}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.creation_date}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={0}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						<IconButton
							onClick={() => {
								tenderPageContext?.setTenderActivityModal({
									tenderId: tender.id,
									tenderName: tender.name,
								});
							}}
						>
							<CommentIcon sx={{ color: primaryColor }} />
						</IconButton>
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={"" + tender?.historical_data_count}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography
						variant="subtitle2"
						component="p"
						sx={{ display: "flex", alignItems: "center" }}
					>
						<If condition={tender?.historical_data_count}>
							<FileIcon
								sx={{
									fontSize: 20,
									color: primaryColor,
								}}
							/>
						</If>
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={"" + tender?.latest_renewex_submission?.status}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography
						variant="subtitle2"
						component="p"
						sx={{ display: "flex", alignItems: "center" }}
					>
						<ParserSubmissionStatusBadge
							submission={tender?.latest_renewex_submission}
						/>
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={"" + tender?.latest_renewex_submission?.status}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography
						variant="subtitle2"
						component="p"
						sx={{ display: "flex", alignItems: "center" }}
					>
						<ParserSubmissionStatusBadge
							submission={tender?.latest_extruder_submission}
						/>
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={tender?.cor_opportunity_id}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					{tender?.cor_opportunity_link ? (
						<MaterialLink
							title={tender?.cor_opportunity_id}
							target="_blank"
							href={tender?.cor_opportunity_link}
						>
							{tender?.cor_opportunity_id}
						</MaterialLink>
					) : (
						<Typography variant="subtitle2" component="p">
							{tender?.cor_opportunity_id}
						</Typography>
					)}
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={tender?.pricing_stage}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.pricing_stage}
					</Typography>
				</FlashUpdateTableCell>

				<FlashUpdateTableCell
					field={tender?.has_negative_price_clause}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.has_negative_price_clause ? (
							<CheckIcon sx={style.checkICon} />
						) : (
							<ClearIcon sx={style.nokIcon} />
						)}
					</Typography>
				</FlashUpdateTableCell>
				<FlashUpdateTableCell
					field={tender?.is_balancing_responsible_party}
					isUpdate={tender?.isNewUpdate}
					sx={style.tableCell}
				>
					<Typography variant="subtitle2" component="p">
						{tender?.is_balancing_responsible_party ? (
							<CheckIcon sx={style.checkICon} />
						) : (
							<ClearIcon sx={style.nokIcon} />
						)}
					</Typography>
				</FlashUpdateTableCell>
				<TableCell sx={style.menuCell}>
					<TenderActionMenu tender={tender} />
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={style.collapsibleCell} colSpan={colsLength + 1}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box sx={style.pricingTableContainer}>
							<PricingTable
								isLoading={pricingsAreLoading}
								pricings={tender.pricings}
								tenderId={tender.id}
							/>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}
